import { S, save, stampItem, tombstoneId } from '../core/state.js';
import { fU, fByCur, uid, esc, toUSD, dividendUSD, isPassiveDividend } from '../core/utils.js';
import { tdAccrued, blendedAvgCost, holdingAfterExit } from '../core/math.js';
import { toast, clMo } from '../ui/layout.js';
import { uiConfirm } from '../ui/dialog.js';
import { lg } from '../modules/history.js';
import { renderP } from '../modules/portfolio.js';
import { renderC } from '../modules/cash.js';

function holdingDividends(ticker) {
  const divs = (S.dividends || []).filter(d => d.ticker === ticker);
  const total = divs.reduce((s, d) => s + (d.net || 0), 0);
  return { count: divs.length, total, items: divs };
}

/** Employer-match records (SAP plan reclass 2026-07-21): income, but NOT passive —
 *  match dies with employment/contribution, so it never enters yield or FI passive-income. */
const isMatch = d => !!d && !isPassiveDividend(d);

/** Total monthly passive income from dividends (USD) — rolling 12-month window, match excluded */
function dividendMonthlyUSD() {
  if (!S.dividends?.length) return 0;
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - 1);
  const cutStr = cutoff.toISOString().slice(0, 10);
  let total = 0;
  S.dividends.forEach(d => {
    if (isMatch(d)) return;
    if (d.date >= cutStr) total += dividendUSD(d);
  });
  return total / 12;
}

/** Passive income summary: dividends + TD interest */
function passiveIncomeSummary() {
  const divMonthly = dividendMonthlyUSD();
  // TD accrued interest — import dynamically to avoid circular
  let tdMonthly = 0;
  try {
    // S.trades with type TD, accrued interest
    // Was `principal * rate/12` — simple, GROSS, and ignoring compounding, while Goals
    // reported the same deposits NET of withholding. The same TD produced two different
    // "TD interest per month" figures on two pages. Both now go through tdAccrued.
    // (audit 2026-08-01)
    const tdTrades = (S.trades || []).filter(t => t.type === 'TimeDeposit' && t.status !== 'Closed');
    tdTrades.forEach(t => {
      const td = t.td; if (!td) return;
      const cur = t.currency || 'TRY';
      const acc = tdAccrued(td);
      if (!acc) return;
      if (acc.monthlyNet != null) tdMonthly += toUSD(acc.monthlyNet, cur);
      else if (acc.dailyNet != null) tdMonthly += toUSD(acc.dailyNet * 30, cur);
      else if (acc.daysOpen > 0) tdMonthly += toUSD((acc.accruedNet || 0) / acc.daysOpen * 30, cur);
    });
  } catch (_e) { /* ignore */ }

  const totalMonthly = divMonthly + tdMonthly;
  const totalAnnual = totalMonthly * 12;

  // Dividend breakdown by ticker (last 12 months)
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - 1);
  const cutStr = cutoff.toISOString().slice(0, 10);
  const byTicker = {};
  (S.dividends || []).forEach(d => {
    if (isMatch(d)) return; // employer match is not passive income
    if (d.date >= cutStr) {
      if (!byTicker[d.ticker]) byTicker[d.ticker] = { ticker: d.ticker, total: 0, count: 0, currency: d.currency };
      byTicker[d.ticker].total += dividendUSD(d);
      byTicker[d.ticker].count++;
    }
  });

  return {
    divMonthly, tdMonthly, totalMonthly, totalAnnual,
    byTicker: Object.values(byTicker).sort((a, b) => b.total - a.total),
    divCount: S.dividends?.length || 0
  };
}

function openDivModal(ticker, currency) {
  document.getElementById('dvTicker').value = ticker;
  document.getElementById('dvDate').value = new Date().toISOString().slice(0, 10);
  document.getElementById('dvGross').value = '';
  document.getElementById('dvCur').value = currency || 'USD';
  // Default WHT: US stocks/ETFs 15% (TR-US treaty), TR holdings 10%, crypto 0
  const h = S.holdings.find(x => x.ticker === ticker);
  const _tp = h?.type || 'Stock'; const cur = currency || h?.currency || 'USD';
  const defaultWht = cur === 'TRY' ? 10 : cur === 'USD' ? 15 : 15;
  document.getElementById('dvWht').value = defaultWht;
  document.getElementById('dvNet').value = '';
  document.getElementById('dvNote').value = '';
  document.getElementById('dvDrip').checked = true; // default: reinvest
  const rp = document.getElementById('dvReinvestPrice');
  if (rp) rp.value = (S.prices[ticker] && S.prices[ticker].price) ? S.prices[ticker].price : '';
  document.getElementById('dvPreview').innerHTML = '<span style="color:var(--t3)">Enter gross amount...</span>';
  document.getElementById('moDiv').classList.add('active');
  calcDvNet();
}

function calcDvNet() {
  const gross = parseFloat(document.getElementById('dvGross').value) || 0;
  const wht = parseFloat(document.getElementById('dvWht').value) || 0;
  const net = gross * (1 - wht / 100);
  document.getElementById('dvNet').value = net ? net.toFixed(2) : '';
  const cur = document.getElementById('dvCur').value;
  // The preview used net * fxRateEUR / fxRate (EUR->TRY->USD) while the record stores
  // toUSD(net, cur), which prefers the DIRECT fxEURUSD. With both rates set they disagree,
  // so the dialog quoted one USD figure and saved another. One function, one answer.
  const netUSD = toUSD(net, cur);
  // Show/hide the reinvest-price field with the DRIP toggle, and preview the auto-bought shares.
  const drip = document.getElementById('dvDrip') ? document.getElementById('dvDrip').checked : false;
  const wrap = document.getElementById('dvReinvestWrap');
  if (wrap) wrap.style.display = drip ? '' : 'none';
  const rprice = parseFloat((document.getElementById('dvReinvestPrice') || {}).value) || 0;
  let preview = gross > 0 ? `Gross: ${fByCur(cur, gross)} → WHT ${wht}% (${fByCur(cur, gross * wht / 100)}) → Net: <strong>${fByCur(cur, net)}</strong> (${fU(netUSD)} USD)` : '';
  if (preview && drip) {
    preview += rprice > 0
      ? ` · DRIP → <strong>${(net / rprice).toFixed(6)}</strong> shares @ ${fByCur(cur, rprice)}`
      : ' · <span style="color:var(--yel)">enter reinvest price to auto-add shares</span>';
  }
  document.getElementById('dvPreview').innerHTML = preview || '<span style="color:var(--t3)">Enter gross amount...</span>';
}

function saveDividend() {
  const ticker = document.getElementById('dvTicker').value;
  const date = document.getElementById('dvDate').value;
  const gross = parseFloat(document.getElementById('dvGross').value);
  const cur = document.getElementById('dvCur').value;
  const wht = parseFloat(document.getElementById('dvWht').value) || 0;
  const net = parseFloat(document.getElementById('dvNet').value);
  let note = document.getElementById('dvNote').value.trim();
  const drip = document.getElementById('dvDrip').checked;
  const isMatchEntry = !!document.getElementById('dvMatch')?.checked;
  const rprice = parseFloat((document.getElementById('dvReinvestPrice') || {}).value) || 0;
  if (!ticker || !gross || !date) return toast('Fill ticker, date, amount', 'a-r');
  if (!S.dividends) S.dividends = [];
  // Auto-fill the note with the reinvested-shares detail (matches the historical "X shares @ price" format)
  // when DRIP + a reinvest price are given and the user left the note blank.
  if (drip && rprice > 0 && net > 0 && !note) note = `${ticker} DRIP — ${(net / rprice).toFixed(6)} shares @ ${fByCur(cur, rprice)}`;
  // F-4: store netUSD at record time so historical passive-income totals don't drift with later FX moves
  const dv = { id: uid(), date, ticker, gross, currency: cur, whtPct: wht, whtAmt: gross * wht / 100, net, netUSD: toUSD(net || 0, cur), note, drip, ...(isMatchEntry ? { kind: 'match' } : {}) };
  stampItem(dv);
  S.dividends.push(dv);

  if (drip) {
    // DRIP: net amount reinvested into same holding — record as dividend income but don't add to cash
    // The user will manually add shares via their regular holding update flow
    // We log a cashTx deposit + immediate withdraw to keep audit trail clean
    const cashCur = cur || 'USD';
    S.cash[cashCur] = (S.cash[cashCur] || 0); // no net change
    S.cashTxs.unshift({ id: uid(), date: date + 'T12:00:00', type: 'deposit', amount: net, currency: cashCur, note: 'Dividend: ' + ticker + (note ? ' — ' + note : '') + ' (DRIP)', _divId: dv.id, _updatedAt: Date.now() });
    S.cashTxs.unshift({ id: uid(), date: date + 'T12:01:00', type: 'withdraw', amount: -net, currency: cashCur, note: 'DRIP reinvest: ' + ticker, _divId: dv.id, _updatedAt: Date.now() });
    // Auto-add the reinvested shares as a 'drip' lot — IDENTICAL to the manual saveAddLot('drip') path:
    // type 'drip' (counts toward qty, EXCLUDED from cost basis), qty = net / reinvest-price.
    if (rprice > 0 && net > 0) {
      const cands = (S.holdings || []).filter(h => h.ticker === ticker);
      const h = cands.find(x => (x.currency || '') === cur) || cands[0];
      if (h) {
        if (!h.lots) h.lots = [];
        const q = net / rprice;
        const lotId = uid();
        // Blend: free shares add quantity and no cost, so the average per share owned falls.
        // Recomputing from the lot list would discard any basis the lots do not describe.
        const _prevQty = h.qty || 0;
        h.avgCost = blendedAvgCost(_prevQty, h.avgCost, { qty: q, price: rprice, cost: q * rprice, type: 'drip' });
        h.lots.push({ id: lotId, date, type: 'drip', qty: q, price: rprice, cost: q * rprice });
        h.qty = _prevQty + q;
        stampItem(h);
        dv.lotId = lotId; dv.lotHoldingId = h.id; // link so deleting the dividend cleanly removes the lot
      } else {
        toast('Dividend saved — no holding matched "' + ticker + '", add the lot manually', 'a-w');
      }
    } else {
      toast('Dividend saved (DRIP) — set a reinvest price next time to auto-add shares', 'a-w');
    }
  } else {
    // Non-DRIP: net dividend added to cash balance
    const cashCur = cur || 'USD';
    S.cash[cashCur] = (S.cash[cashCur] || 0) + net;
    S.cashTxs.unshift({ id: uid(), date: date + 'T12:00:00', type: 'deposit', amount: net, currency: cashCur, note: 'Dividend: ' + ticker + (note ? ' — ' + note : ''), _divId: dv.id, _updatedAt: Date.now() });
  }

  // History log
  if (typeof lg === 'function') lg('dividend', ticker, 'Gross ' + fByCur(cur, gross) + ' WHT ' + wht + '% Net ' + fByCur(cur, net) + (drip ? ' (DRIP)' : ''));
  save(); clMo('moDiv');
  if (typeof renderP === 'function') renderP();
  if (typeof renderC === 'function') renderC();
  renderDividendList();
  toast(ticker + ' dividend recorded' + (drip ? ' (DRIP)' : ''), 'a-g');
}

/** Standalone dividend modal — opens with ticker selector instead of readonly */
function openDivModalStandalone() {
  const tickerEl = document.getElementById('dvTicker');
  tickerEl.value = '';
  tickerEl.readOnly = false;
  tickerEl.placeholder = 'Enter ticker (e.g. VOO, AKBNK)';
  document.getElementById('dvDate').value = new Date().toISOString().slice(0, 10);
  document.getElementById('dvGross').value = '';
  document.getElementById('dvCur').value = 'USD';
  document.getElementById('dvWht').value = 15;
  document.getElementById('dvNet').value = '';
  document.getElementById('dvNote').value = '';
  document.getElementById('dvDrip').checked = true;
  const rp0 = document.getElementById('dvReinvestPrice'); if (rp0) rp0.value = '';
  document.getElementById('dvPreview').innerHTML = '<span style="color:var(--t3)">Enter gross amount...</span>';
  document.getElementById('moDiv').classList.add('active');
  calcDvNet();
}

/** Standalone modal: when the user types a known ticker, prefill currency + live reinvest price + WHT. */
function dvTickerLookup() {
  const el = document.getElementById('dvTicker');
  if (!el || el.readOnly) return;
  const tk = el.value.trim().toUpperCase();
  const h = (S.holdings || []).find(x => (x.ticker || '').toUpperCase() === tk);
  if (h) {
    document.getElementById('dvCur').value = h.currency || 'USD';
    const rp = document.getElementById('dvReinvestPrice');
    if (rp && !rp.value) rp.value = (S.prices[h.ticker] && S.prices[h.ticker].price) ? S.prices[h.ticker].price : '';
  }
  calcDvNet();
}

/** Render dividend list table in Cash page */
function renderDividendList() {
  const body = document.getElementById('divListBody');
  const sumRow = document.getElementById('divSummaryRow');
  if (!body) return;

  const divs = [...(S.dividends || [])].sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  if (!divs.length) {
    body.innerHTML = '<tr><td colspan="9" class="neo-empty">No dividends recorded yet</td></tr>';
    if (sumRow) sumRow.innerHTML = '';
    return;
  }

  // Summary
  let totalNetUSD = 0, totalGrossUSD = 0, totalWhtUSD = 0;
  divs.forEach(d => {
    const cur = d.currency || 'USD';
    // F-4: prefer stored netUSD (immutable at record time); use canonical toUSD for fallback/gross/wht (fixes EUR cross-rate bug)
    totalNetUSD += dividendUSD(d);
    totalGrossUSD += toUSD(d.gross || 0, cur);
    totalWhtUSD += toUSD(d.whtAmt || 0, cur);
  });

  if (sumRow) {
    sumRow.innerHTML = `
      <div style="flex:1;text-align:center"><div style="font-size:16px;font-weight:700;color:var(--acc)">${fU(totalNetUSD)}</div><div style="font-size:9px;color:var(--t3)">Total Net (USD)</div></div>
      <div style="flex:1;text-align:center"><div style="font-size:14px;color:var(--t2)">${fU(totalGrossUSD)}</div><div style="font-size:9px;color:var(--t3)">Total Gross</div></div>
      <div style="flex:1;text-align:center"><div style="font-size:14px;color:var(--neg,#E24B4A)">${fU(totalWhtUSD)}</div><div style="font-size:9px;color:var(--t3)">WHT Paid</div></div>
      <div style="flex:1;text-align:center"><div style="font-size:14px;color:var(--t1)">${divs.length}</div><div style="font-size:9px;color:var(--t3)">Records</div></div>
    `;
  }

  body.innerHTML = divs.map(d => `<tr>
    <td style="font-size:11px">${esc(d.date || '')}</td>
    <td><strong>${esc(d.ticker || '')}</strong></td>
    <td style="font-size:10px">${esc(d.currency || '')}</td>
    <td class="tr" style="font-size:11px">${fByCur(d.currency, d.gross || 0)}</td>
    <td class="tr" style="font-size:11px;color:var(--neg,#E24B4A)">${d.whtPct || 0}%</td>
    <td class="tr" style="font-size:11px;font-weight:600;color:var(--acc)">${fByCur(d.currency, d.net || 0)}</td>
    <td style="font-size:10px;text-align:center">${d.drip ? '<span style="color:var(--acc)" title="Reinvested into same holding">DRIP</span>' : '<span style="color:var(--t3)">Cash</span>'}</td>
    <td style="font-size:10px;color:var(--t3)">${esc(d.note || '')}</td>
    <td><button class="btn btn-s btn-d" style="font-size:9px;padding:1px 4px" data-action="deleteDividend" data-args='["${d.id}"]' title="Delete">×</button></td>
  </tr>`).join('');
}

/** Delete a dividend entry */
async function deleteDividend(id) {
  const ok = await uiConfirm('Delete this dividend record? Its cash effect and linked cash transactions are reversed.', { title: 'Delete dividend', okText: 'Delete' });
  if (!ok) return;
  const idx = (S.dividends || []).findIndex(d => d.id === id);
  if (idx < 0) return toast('Dividend not found', 'a-r');
  const dv = S.dividends[idx];

  // Collect ids to tombstone so the deletion survives multi-device merge (mergeArrays union)
  const tombIds = [dv.id];

  // Reverse cash effect
  if (!dv.drip) {
    const cashCur = dv.currency || 'USD';
    S.cash[cashCur] = (S.cash[cashCur] || 0) - (dv.net || 0);
  }
  // Remove related cashTxs. Prefer the precise _divId link (new records); only fall back to the
  // note-substring match for LEGACY records that predate _divId — so deleting one dividend no longer
  // wipes other same-ticker dividends' cash txs (D-7). Capture removed ids for tombstoning.
  const _tk = (dv.ticker || '').toLowerCase();
  const _hasTagged = (S.cashTxs || []).some(tx => tx._divId === dv.id);
  const _day = v => String(v || '').slice(0, 10);
  const _near = (x, y) => Math.abs(Math.abs(x) - Math.abs(y)) < 0.01;
  S.cashTxs = (S.cashTxs || []).filter(tx => {
    let isRelated;
    if (_hasTagged) {
      isRelated = tx._divId === dv.id;
    } else {
      // THE FALLBACK, NARROWED. It used to match on the ticker substring alone, so on a book
      // with eleven monthly SAP dividends — every one of them noted `Dividend: SAP (DRIP)` —
      // deleting ONE deleted all twenty-two rows. That was the live shape on 2026-08-14: not a
      // single cash row carried a `_divId`, so every delete took this branch. v50 back-links
      // them, and this stays for anything v50 found ambiguous: same ticker AND same day AND
      // same amount, which is the tuple that identifies one dividend. (D-7, again)
      const n = (tx.note || '').toLowerCase();
      isRelated = !tx._divId
        && (n.startsWith('dividend: ' + _tk) || n.startsWith('drip reinvest: ' + _tk))
        && _day(tx.date) === _day(dv.date)
        && _near(tx.amount, dv.net);
    }
    if (isRelated && tx.id) tombIds.push(tx.id);
    return !isRelated;
  });

  // Reverse the auto-added DRIP lot (precise, by id) so deleting the dividend leaves no orphan shares
  if (dv.lotId) {
    const h = (S.holdings || []).find(x => x.id === dv.lotHoldingId) || (S.holdings || []).find(x => (x.lots || []).some(l => l.id === dv.lotId));
    if (h && h.lots) {
      // INCREMENTAL, never recomputed from the lot list. Same defect as execExitH had, same
      // fix: a holding can carry basis its lots do not describe, and on 2026-08-14 seven of
      // them did. Recomputing here collapsed the position to whatever the lots happened to
      // describe — deleting one DRIP dividend on TEJ would have destroyed 18,126.74 units
      // carrying TRY 37,410 of basis, on a record that owns none of them.
      //
      // exitCost 0 because a DRIP lot adds quantity and no cost (blendedAvgCost). Removing it
      // RAISES the average, which is the dilution running backwards. (RET-001 / CONTRACT-02)
      const _lot = h.lots.find(l => l.id === dv.lotId);
      const _lq = (_lot && +_lot.qty) || 0;
      h.lots = h.lots.filter(l => l.id !== dv.lotId);
      const _after = holdingAfterExit(h.qty, h.avgCost, _lq, 0);
      h.qty = _after.qty;
      if (h.qty > 0) h.avgCost = _after.avgCost;
      stampItem(h);
    }
    tombIds.push(dv.lotId);
  }

  S.dividends.splice(idx, 1);

  // Tombstone every removed id so a cloud merge cannot resurrect it. `tombstoneId` is the one
  // primitive — it also dates the id, and the 1,000-tombstone retention cap sorts by that date.
  tombIds.forEach(tombstoneId);

  save();
  renderDividendList();
  if (typeof renderC === 'function') renderC();
  if (typeof renderP === 'function') renderP();
  toast('Dividend deleted', 'a-y');
}

export { holdingDividends, dividendMonthlyUSD, passiveIncomeSummary, openDivModal, openDivModalStandalone, calcDvNet, dvTickerLookup, saveDividend, renderDividendList, deleteDividend };
